import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import AppSidebar from '@/components/AppSidebar'
import CreateMeetingModalHost from '@/components/CreateMeetingModalHost'
import SaveToastHost from '@/components/SaveToastHost'
import { useLocaleSync } from '@/hooks/useUiStrings'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { useStore } from '@/store/useStore'
import { useUiStrings } from '@/hooks/useUiStrings'

const MD_UP = '(min-width: 768px)'

export default function AppLayout() {
  const location = useLocation()
  const { t } = useUiStrings()
  useLocaleSync()
  const isMdUp = useMediaQuery(MD_UP)
  const theme = useStore((s) => s.theme)
  const sidebarOpen = useStore((s) => s.sidebarOpen)
  const setSidebarOpen = useStore((s) => s.setSidebarOpen)
  /**
   * Set once, and only when the app booted inside a Teams call. Not persisted,
   * so an ordinary browser tab never inherits it.
   */
  const isTeamsPanel = useStore((s) => s.isTeamsPanel)

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark')
  }, [theme])

  useEffect(() => {
    if (typeof window !== 'undefined' && !window.matchMedia(MD_UP).matches) {
      setSidebarOpen(false)
    }
  }, [setSidebarOpen])

  useEffect(() => {
    if (!isMdUp) setSidebarOpen(false)
  }, [location.pathname, isMdUp, setSidebarOpen])

  const closeSidebar = () => setSidebarOpen(false)
  const handleNavigate = () => {
    if (!isMdUp) closeSidebar()
  }

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-[var(--bg-chat)] text-[var(--text-primary)]">
      {!isMdUp && sidebarOpen && (
        <button
          type="button"
          aria-label={t.closeSidebar}
          className="fixed inset-0 z-40 bg-black/40 motion-safe:animate-fade-in"
          onClick={closeSidebar}
        />
      )}

      {/* Left out rather than hidden, and the toggle goes with it.
          `sidebarOpen` defaults to true and is persisted, so a wide Teams
          context (`meetingStage`) would otherwise open on a 240px rail of our
          chrome beside the host's, with the only control for it hidden.

          The backdrop above needs no guard of its own: `toggleSidebar` is the
          only thing that sets `sidebarOpen` true and `SidebarToggle` is its
          only caller, so with that gone nothing can open the sidebar here —
          every other write closes it, and the mount effect above closes it
          outright at panel widths. */}
      {!isTeamsPanel && (
      <aside
        className={[
          'fixed inset-y-0 left-0 z-50 flex shrink-0 flex-col border-r border-[var(--border-color)] bg-[var(--bg-secondary)]',
          'transition-[transform,width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] will-change-[transform,width]',
          'md:relative md:translate-x-0',
          sidebarOpen
            ? 'w-[240px] translate-x-0'
            : 'w-[240px] -translate-x-full md:w-0 md:overflow-hidden md:border-r-0',
        ].join(' ')}
        aria-hidden={!sidebarOpen}
      >
        <div className="flex h-full w-[240px] flex-col overflow-hidden">
          <AppSidebar onNavigate={handleNavigate} onClose={closeSidebar} />
        </div>
      </aside>
      )}

      <main className="flex min-h-0 min-w-0 flex-1 flex-col">
        <Outlet />
      </main>

      <CreateMeetingModalHost />
      <SaveToastHost />
    </div>
  )
}
